"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { CheckCircle, Clock, ShoppingBag, X, Download, ArrowLeft } from "lucide-react"
import { useApp } from "@/lib/context"
import { generateInvoicePDF } from "@/lib/invoice-generator"
import { useRouter } from "next/navigation"
import { useOrderPolling } from "@/hooks/use-order-polling"

export default function OrderCompletionModal() {
  const {
    isOrderCompleted,
    completeOrder,
    orderStatus,
    customerAuth,
    t,
    isValidSession,
    acknowledgeOrder,
    isOrderAcknowledged,
    localId,
    estabelecimentoId,
  } = useApp()

  const router = useRouter()

  const [showModal, setShowModal] = useState(false)
  const hasShownRef = useRef(false)
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Long polling para verificar status do pedido
  const {
    orderStatus: pollingStatus,
    isPolling,
    isLoading: isPollingLoading,
    error: pollingError,
  } = useOrderPolling(
    orderStatus?._id || null,
    {
      pollingInterval: 5000, // 5 segundos
      maxAttempts: 120, // Máx 10 minutos
      secaoId: localId || undefined,
      estabelecimentoId: estabelecimentoId || undefined,
    }
  )

  // Usar o status do polling se disponível, senão usar o status da app
  const currentOrderStatus = pollingStatus || orderStatus

  // Mostrar modal apenas quando o pedido for realmente completado
  useEffect(() => {
    if (!currentOrderStatus) {
      setShowModal(false)
      hasShownRef.current = false
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
      }
      return
    }

    const statusFinalizado =
      currentOrderStatus.status === "Entregue" || currentOrderStatus.status === "Finalizado"

    const alreadyAck = isOrderAcknowledged(currentOrderStatus._id)

    // Verificar se já mostramos este modal para evitar reabertura
    const modalKey = `modal_shown_${currentOrderStatus._id}`
    const hasShown = localStorage.getItem(modalKey) === 'true'
    
    if (statusFinalizado && !alreadyAck && !hasShown && !hasShownRef.current) {
      // Marcar como mostrado IMEDIATAMENTE para prevenir reabertura
      localStorage.setItem(modalKey, 'true')
      hasShownRef.current = true
      setShowModal(true)
      
      // Auto-fechar após 10 segundos
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
      }
      autoCloseTimerRef.current = setTimeout(() => {
        handleClose()
      }, 10000)
    } else {
      setShowModal(false)
    }
  }, [currentOrderStatus, isOrderAcknowledged])

  const handleClose = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current)
      autoCloseTimerRef.current = null
    }

    if (currentOrderStatus && currentOrderStatus._id) {
      // Marcar como reconhecido ANTES de fechar
      acknowledgeOrder(currentOrderStatus._id)
    }
    
    setShowModal(false)
    // Chamar completeOrder para resetar o estado
    completeOrder()
  }, [currentOrderStatus, acknowledgeOrder, completeOrder])

  // Fechar modal ao pressionar ESC
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showModal) {
        handleClose()
      }
    }
    
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
      }
    }
  }, [showModal, handleClose])

  const handleNewOrder = () => {
    handleClose()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleBackToHome = () => {
    handleClose()
    // Fazer logout/resetar sessão
    router.push("/")
  }

  const handleDownloadPDF = () => {
    if (!orderStatus) return

    try {
      generateInvoicePDF({
        order: orderStatus,
        customerName: customerAuth?.name || "Cliente",
        customerPhone: customerAuth?.phoneNumber || "",
        paymentMethod: "Dinheiro",
      })
    } catch (error) {
      console.error("Erro ao gerar PDF:", error)
    }
  }

  // Se não tiver pedido ou modal não deve ser mostrado, retorna null
  if (!showModal || !currentOrderStatus) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-background text-foreground rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden shadow-2xl relative border border-border"
        >
          {/* Header com botão de fechar */}
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={handleClose}
              className="w-10 h-10 bg-background/90 backdrop-blur-md rounded-full flex items-center justify-center text-muted-foreground hover:bg-background hover:text-foreground transition-all duration-300 shadow-lg border border-border"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Conteúdo principal */}
          <div className="p-8 text-center flex-1 min-h-0 overflow-y-auto">
            {/* Ícone de sucesso animado */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2,
              }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 relative"
            >
              <CheckCircle className="w-12 h-12 text-green-600" />
            </motion.div>

            {/* Título e mensagem */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <h2 className="text-2xl font-bold text-foreground mb-2">Pedido Entregue!</h2>
              <p className="text-muted-foreground mb-6">
                Obrigado por escolher nosso restaurante. Esperamos que tenha uma excelente experiência!
              </p>
            </motion.div>

            {/* Informações do pedido */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="bg-muted rounded-xl p-4 mb-6"
            >
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
                <ShoppingBag className="w-4 h-4" />
                <span>Pedido #{currentOrderStatus._id?.slice(-6)}</span>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>
                  Entregue às{" "}
                  {new Date().toLocaleTimeString("pt-AO", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </motion.div>

            {/* Botões de ação */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="space-y-3"
            >
              {/* Botão de download PDF */}
              <button
                onClick={handleDownloadPDF}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-muted text-foreground rounded-xl hover:bg-muted/80 transition-colors"
              >
                <Download className="w-4 h-4" />
                Baixar Comprovante
              </button>

              {/* Botão de novo pedido */}
              <button
                onClick={handleNewOrder}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-all duration-300"
              >
                <ShoppingBag className="w-4 h-4" />
                Fazer Novo Pedido
              </button>

              {/* Botão de voltar para página inicial */}
              <button
                onClick={handleBackToHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-secondary text-secondary-foreground rounded-xl hover:opacity-90 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para Início
              </button>
            </motion.div>

            {/* Mensagem de fechamento */}
            <div className="mt-6 text-xs text-muted-foreground">
              Clique em qualquer botão ou no <span className="font-medium">X</span> para continuar
            </div>

            {/* Indicador de polling */}
            {isPolling && (
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span>Verificando status...</span>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}